import React, { useState, useRef, useEffect, useMemo } from 'react'

// ─── Skills Library ─────────────────────────────────────────────────────────
export const SKILLS_LIBRARY: Record<string, string[]> = {
  'Programming Languages': [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C', 'C++', 'C#', 'PHP',
    'Ruby', 'Swift', 'Kotlin', 'Go', 'Rust', 'R', 'MATLAB', 'Dart', 'Scala',
    'Perl', 'HTML', 'CSS', 'SQL', 'Bash', 'PowerShell', 'Assembly',
  ],
  'Web Development': [
    'React', 'Angular', 'Vue.js', 'Next.js', 'Node.js', 'Express.js',
    'Django', 'Flask', 'Spring Boot', 'Laravel', 'ASP.NET', 'Ruby on Rails',
    'REST APIs', 'GraphQL', 'Tailwind CSS', 'Bootstrap', 'jQuery',
    'WordPress', 'Shopify', 'Responsive Design', 'Web Accessibility',
  ],
  'Mobile Development': [
    'React Native', 'Flutter', 'Swift/iOS', 'Kotlin/Android',
    'Xamarin', 'Ionic', 'Mobile UI Design', 'App Store Optimization',
  ],
  'Data & Analytics': [
    'Data Analysis', 'Data Visualization', 'Machine Learning', 'Deep Learning',
    'Natural Language Processing', 'Computer Vision', 'Statistics',
    'Data Mining', 'Big Data', 'ETL', 'Business Intelligence',
    'Power BI', 'Tableau', 'Excel Advanced', 'Google Analytics',
    'A/B Testing', 'Predictive Modeling', 'Data Warehousing',
  ],
  'Databases': [
    'MySQL', 'PostgreSQL', 'MongoDB', 'SQLite', 'Oracle DB',
    'Microsoft SQL Server', 'Redis', 'Firebase', 'Supabase',
    'DynamoDB', 'Cassandra', 'Neo4j', 'Database Design',
  ],
  'Cloud & DevOps': [
    'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes',
    'CI/CD', 'Jenkins', 'GitHub Actions', 'Terraform', 'Ansible',
    'Linux Administration', 'Nginx', 'Apache', 'Load Balancing',
    'Microservices', 'Serverless', 'Cloud Architecture',
  ],
  'Cybersecurity': [
    'Network Security', 'Penetration Testing', 'Ethical Hacking',
    'Vulnerability Assessment', 'Incident Response', 'SIEM',
    'Firewall Management', 'Encryption', 'Security Auditing',
    'Compliance (ISO 27001)', 'Risk Assessment',
  ],
  'Networking': [
    'TCP/IP', 'DNS', 'DHCP', 'VPN', 'LAN/WAN', 'Network Troubleshooting',
    'Cisco Networking', 'Wireless Networking', 'Network Monitoring',
    'Router/Switch Configuration', 'VoIP', 'SDN',
  ],
  'Design & Multimedia': [
    'UI/UX Design', 'Graphic Design', 'Adobe Photoshop', 'Adobe Illustrator',
    'Adobe Premiere Pro', 'Adobe After Effects', 'Figma', 'Canva',
    'Video Editing', 'Motion Graphics', '3D Modeling', 'Wireframing',
    'Prototyping', 'User Research', 'Design Thinking',
  ],
  'Project Management': [
    'Project Management', 'Agile/Scrum', 'Kanban', 'Waterfall',
    'JIRA', 'Trello', 'Asana', 'MS Project', 'Risk Management',
    'Stakeholder Management', 'Budgeting', 'Resource Planning',
    'Product Management', 'Program Management',
  ],
  'Business & Finance': [
    'Accounting', 'Financial Analysis', 'Bookkeeping', 'Auditing',
    'Tax Preparation', 'Business Planning', 'Market Research',
    'Business Development', 'Strategic Planning', 'Supply Chain Management',
    'Procurement', 'Inventory Management', 'Cost Analysis',
    'QuickBooks', 'SAP', 'Enterprise Resource Planning',
  ],
  'Marketing & Sales': [
    'Digital Marketing', 'Social Media Marketing', 'SEO', 'SEM',
    'Content Marketing', 'Email Marketing', 'Copywriting',
    'Brand Management', 'Market Analysis', 'Sales Strategy',
    'CRM (Salesforce)', 'Google Ads', 'Facebook Ads',
    'Influencer Marketing', 'Public Relations', 'Event Planning',
  ],
  'Engineering': [
    'AutoCAD', 'SolidWorks', 'MATLAB', 'Circuit Design', 'PLC Programming',
    'Mechanical Design', 'Structural Analysis', 'Quality Control',
    'Manufacturing Processes', 'Technical Drawing', 'HVAC',
    'Renewable Energy', 'Robotics', 'IoT', '3D Printing',
    'Electrical Wiring', 'Process Engineering',
  ],
  'Healthcare & Nursing': [
    'Patient Care', 'Clinical Assessment', 'Vital Signs Monitoring',
    'Medication Administration', 'IV Therapy', 'Wound Care',
    'Emergency Response', 'CPR/BLS', 'Health Education',
    'Medical Records Management', 'Infection Control',
    'Mental Health Support', 'Community Health', 'Telemedicine',
  ],
  'Education & Training': [
    'Curriculum Development', 'Lesson Planning', 'Classroom Management',
    'Educational Technology', 'Student Assessment', 'Special Education',
    'Online Teaching', 'Instructional Design', 'Mentoring',
    'Training Development', 'E-Learning', 'Academic Research',
  ],
  'Hospitality & Tourism': [
    'Customer Service', 'Food & Beverage Management', 'Hotel Management',
    'Event Coordination', 'Tourism Planning', 'Reservation Systems',
    'Food Safety', 'Menu Planning', 'Banquet Management',
    'Front Office Operations', 'Housekeeping Management',
  ],
  'Maritime': [
    'Navigation', 'Ship Operations', 'Marine Engineering',
    'Safety Management', 'Cargo Handling', 'Seamanship',
    'Maritime Law', 'Weather Routing', 'Port Management',
    'Marine Electronics', 'GMDSS', 'Vessel Maintenance',
  ],
  'Communication & Soft Skills': [
    'Communication', 'Public Speaking', 'Presentation Skills',
    'Technical Writing', 'Report Writing', 'Negotiation',
    'Conflict Resolution', 'Critical Thinking', 'Problem Solving',
    'Time Management', 'Leadership', 'Teamwork', 'Adaptability',
    'Attention to Detail', 'Creativity', 'Emotional Intelligence',
    'Decision Making', 'Multitasking', 'Work Ethic',
  ],
  'Languages': [
    'English Proficiency', 'Filipino/Tagalog', 'Cebuano/Bisaya',
    'Mandarin Chinese', 'Japanese', 'Korean', 'Spanish',
    'French', 'German', 'Arabic', 'Technical English',
  ],
}

// Flat list for search
const ALL_SKILLS = Object.entries(SKILLS_LIBRARY).flatMap(([category, skills]) =>
  skills.map(skill => ({ skill, category }))
)

// ─── Component Props ────────────────────────────────────────────────────────
interface SkillsPickerProps {
  /** Currently selected skills */
  value: string[]
  /** Called when skills change */
  onChange: (skills: string[]) => void
  /** Max number of skills allowed */
  maxSkills?: number
  /** Max characters per skill (for custom skills) */
  maxSkillLength?: number
  /** Placeholder text for the search input */
  placeholder?: string
  /** Whether the picker is disabled (view-only mode) */
  disabled?: boolean
  /** Optional label above the component */
  label?: string
  /** Optional helper text below the label */
  helperText?: string
  /** Show required asterisk */
  required?: boolean
}

export const SkillsPicker: React.FC<SkillsPickerProps> = ({
  value,
  onChange,
  maxSkills = 30,
  maxSkillLength = 60,
  placeholder = 'Search skills or type a custom one…',
  disabled = false,
  label,
  helperText,
  required = false,
}) => {
  const [query, setQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const atLimit = value.length >= maxSkills

  // Filter skills based on search query
  const filteredSkills = useMemo(() => {
    if (!query.trim()) return []
    const q = query.toLowerCase()
    return ALL_SKILLS.filter(
      ({ skill }) =>
        skill.toLowerCase().includes(q) && !value.includes(skill)
    ).slice(0, 20)
  }, [query, value])

  // Category view: skills not yet selected
  const categorySkills = useMemo(() => {
    if (!activeCategory) return []
    return (SKILLS_LIBRARY[activeCategory] || []).filter(s => !value.includes(s))
  }, [activeCategory, value])

  const addSkill = (skill: string) => {
    const trimmed = skill.trim()
    if (!trimmed || atLimit) return
    if (trimmed.length > maxSkillLength) return
    if (value.includes(trimmed)) return
    onChange([...value, trimmed])
    setQuery('')
    inputRef.current?.focus()
  }

  const removeSkill = (skill: string) => {
    if (disabled) return
    onChange(value.filter(s => s !== skill))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If there are filtered results, add the first one; otherwise add custom
      if (filteredSkills.length > 0) {
        addSkill(filteredSkills[0].skill)
      } else if (query.trim()) {
        addSkill(query)
      }
    }
    if (e.key === 'Backspace' && !query && value.length > 0) {
      removeSkill(value[value.length - 1])
    }
    if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const isSearching = query.trim().length > 0

  return (
    <div ref={containerRef} className="relative">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {helperText && (
        <p className="text-xs text-gray-500 mb-2">{helperText}</p>
      )}

      {/* Selected skills tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map(skill => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
            >
              {skill}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeSkill(skill)}
                  className="text-blue-400 hover:text-blue-600 ml-0.5"
                  aria-label={`Remove ${skill}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      {!disabled && (
        <>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => {
                  setQuery(e.target.value)
                  setShowDropdown(true)
                  if (e.target.value.trim()) setActiveCategory(null)
                }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                maxLength={maxSkillLength}
                disabled={atLimit}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={atLimit ? `Maximum ${maxSkills} skills reached` : placeholder}
              />
            </div>
            {query.trim() && (
              <button
                type="button"
                onClick={() => addSkill(query)}
                disabled={atLimit}
                className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Add
              </button>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && !atLimit && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden">
              {isSearching ? (
                /* Search results */
                <div className="overflow-y-auto max-h-72">
                  {filteredSkills.length > 0 ? (
                    filteredSkills.map(({ skill, category }) => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => addSkill(skill)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between text-sm"
                      >
                        <span className="text-gray-900">{skill}</span>
                        <span className="text-xs text-gray-400">{category}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No matching skills found.{' '}
                      <button
                        type="button"
                        onClick={() => addSkill(query)}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Add "{query.trim()}" as custom skill
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Category browser */
                <div className="flex" style={{ minHeight: '200px' }}>
                  {/* Categories sidebar */}
                  <div className="w-48 border-r border-gray-100 overflow-y-auto max-h-72 flex-shrink-0">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Categories
                    </div>
                    {Object.keys(SKILLS_LIBRARY).map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          activeCategory === cat
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  {/* Skills in category */}
                  <div className="flex-1 overflow-y-auto max-h-72">
                    {activeCategory ? (
                      categorySkills.length > 0 ? (
                        <div className="p-2">
                          <div className="flex flex-wrap gap-1.5">
                            {categorySkills.map(skill => (
                              <button
                                key={skill}
                                type="button"
                                onClick={() => addSkill(skill)}
                                className="px-2.5 py-1 text-sm rounded-full border border-gray-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                              >
                                + {skill}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-sm text-gray-400 p-4">
                          All skills in this category are selected
                        </div>
                      )
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-gray-400 p-4">
                        ← Select a category to browse skills
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Counter */}
          <div className="flex justify-between mt-1">
            <p className="text-xs text-gray-400">
              {value.length}/{maxSkills} skills selected
            </p>
            {!isSearching && !showDropdown && (
              <button
                type="button"
                onClick={() => { setShowDropdown(true); inputRef.current?.focus() }}
                className="text-xs text-blue-600 hover:underline"
              >
                Browse skills library
              </button>
            )}
          </div>
        </>
      )}

      {/* Disabled view when no skills */}
      {disabled && value.length === 0 && (
        <p className="text-sm text-gray-500 italic">No skills specified</p>
      )}
    </div>
  )
}

export default SkillsPicker
